

import { ReactNode } from "react";
import {
  Building2,
  Settings,
  Mail,
  Smartphone,
  Download,
  Database,
  FileText,
  Shield,
} from "lucide-react";

const allTabs: Array<{
  id: "business" | "general" | "email" | "backup" | "data" | "pwa" | "documents" | "roles";
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}> = [
  { id: "business", label: "Business Info", icon: Building2 },
  { id: "general", label: "General", icon: Settings },
  { id: "roles", label: "Roles & Access", icon: Shield },
  { id: "email", label: "Email / SMTP", icon: Mail },
  { id: "pwa", label: "PWA / App", icon: Smartphone },
  { id: "documents", label: "Documents", icon: FileText },
  { id: "backup", label: "Backup & Export", icon: Download },
  { id: "data", label: "Data Management", icon: Database },
];

type TabId = typeof allTabs[number]["id"];

interface SettingsSidebarProps {
  activeTab: TabId;
  onTabChange: (tab: TabId) => void;
  loading?: boolean;
  isStaff?: boolean;
  isSuperAdmin?: boolean;
}

export default function SettingsSidebar({
  activeTab,
  onTabChange,
  loading = false,
  isStaff = false,
  isSuperAdmin = false,
}: SettingsSidebarProps) {
  // Staff: hide data management, backup, and roles
  // Non-super-admin: also hide data management
  const tabs = allTabs.filter(tab => {
    if (isStaff && (tab.id === 'data' || tab.id === 'backup' || tab.id === 'roles')) return false;
    if (!isSuperAdmin && tab.id === 'data') return false;
    return true;
  });

  return (
    <div className="lg:w-56 flex-shrink-0">
      <div className="bg-white rounded-xl border border-stone-100 shadow-sm overflow-hidden">
        {tabs.map((tab) => {
          const IconComponent = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => onTabChange(tab.id)}
              disabled={loading}
              className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? "bg-amber-50 text-amber-700 border-r-2 border-amber-500"
                  : "text-stone-600 hover:bg-stone-50"
              } ${loading ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              <IconComponent className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export type { TabId };
